const INK = '#1D2129';
const WHITE = '#FFFFFF';

/** Expands `#abc` to `#aabbcc`; returns null for anything that is not a 3- or 6-digit hex. */
const parseHex = (hex: string): { r: number; g: number; b: number } | null => {
  const value = (hex || '').trim().replace(/^#/, '');
  const full = value.length === 3 ? value.split('').map(c => c + c).join('') : value;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

/** WCAG relative luminance of an sRGB color, 0 (black) to 1 (white). */
export const relativeLuminance = (hex: string): number => {
  const rgb = parseHex(hex);
  if (!rgb) return 1;
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(channel => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two colors, 1 (identical) to 21 (black on white). */
export const contrastRatio = (a: string, b: string): number => {
  const lumA = relativeLuminance(a);
  const lumB = relativeLuminance(b);
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Text color that stays readable on `background`. The staff-group palette spans pale amber to deep
 * red, so a single hardcoded text color would be unreadable on half of it.
 *
 * Compares the two candidates by actual contrast ratio rather than testing luminance against a
 * threshold: mid-tone colors like `#F2803C` sit above a naive 0.5 cut yet still read far better
 * with dark ink (6.1:1) than with white (2.7:1). An unparseable color falls back to dark ink.
 */
export const readableTextOn = (background: string): string =>
  contrastRatio(background, INK) >= contrastRatio(background, WHITE) ? INK : WHITE;

/**
 * Blends `hex` toward black by `amount` (0 = unchanged, 1 = black).
 *
 * Used for a card's border and left stripe, so the edge stays visible against the card's own ground.
 */
export const mixWithBlack = (hex: string, amount: number): string => {
  const rgb = parseHex(hex);
  if (!rgb) return INK;
  const ratio = Math.min(Math.max(amount, 0), 1) || 0;
  const toHex = (channel: number) => Math.round(channel * (1 - ratio)).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`.toUpperCase();
};
